const openedDetails = new WeakSet();
const invalidFieldTimestamps = new WeakMap();

/**
 * Publish privacy-safe PREVURA interaction events through Shopify's Customer
 * Events bridge. Standard commerce events remain owned by Shopify Web Pixels
 * and are deliberately not duplicated here.
 * @param {string} name
 * @param {Record<string, string | boolean>} [payload]
 * @returns {boolean} Whether the event was handed to Shopify with analytics consent.
 */
function publishPrevuraEvent(name, payload = {}) {
  const eventPayload = {
    ...payload,
    path: window.location.pathname,
  };

  document.dispatchEvent(
    new CustomEvent('prevura:measurement', {
      detail: { name, payload: eventPayload },
    })
  );

  const publish = window.Shopify?.analytics?.publish;
  if (typeof publish !== 'function') return false;

  const customerPrivacy = window.Shopify?.customerPrivacy;
  if (
    !customerPrivacy ||
    typeof customerPrivacy.analyticsProcessingAllowed !== 'function'
  ) {
    return false;
  }

  try {
    if (!customerPrivacy.analyticsProcessingAllowed()) return false;
    Promise.resolve(publish(name, eventPayload)).catch(() => {});
    return true;
  } catch {
    // Measurement must never block or break the storefront.
    return false;
  }
}

/**
 * @param {Element} element
 * @returns {string}
 */
function getPlacement(element) {
  if (element.closest('cart-drawer-component')) return 'cart_drawer';
  if (element.closest('.cart-page')) return 'cart_page';
  if (element.closest('header')) return 'header';
  if (element.closest('footer')) return 'footer';
  return 'content';
}

document.addEventListener('change', (event) => {
  const target = event.target;
  if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;

  if (target.matches('[data-pv3-refund-select]')) {
    const selectedOption = target.selectedOptions[0];
    publishPrevuraEvent('prevura:reimbursement_insurer_selected', {
      selection_type: target.value === 'other' ? 'other' : 'listed',
      official_source_available: Boolean(selectedOption?.dataset.source),
    });
    return;
  }

  if (target.matches('[data-prevura-participation-confirmation]')) {
    publishPrevuraEvent('prevura:participation_confirmation_changed', {
      interaction: 'changed',
      placement: getPlacement(target),
    });
    return;
  }

  if (target.matches('[data-prevura-digital-content-consent]')) {
    publishPrevuraEvent('prevura:digital_content_consent_changed', {
      interaction: 'changed',
      placement: getPlacement(target),
    });
  }
});

document.addEventListener('click', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;

  const insurerSource = target.closest('[data-pv3-refund-source]');
  if (insurerSource instanceof HTMLAnchorElement) {
    publishPrevuraEvent('prevura:reimbursement_source_opened', {
      destination_type: 'official_insurer_information',
    });
    return;
  }

  const supportContact = target.closest('[data-prevura-support-contact]');
  if (supportContact instanceof HTMLAnchorElement) {
    publishPrevuraEvent('prevura:support_contact_clicked', {
      channel: supportContact.href.startsWith('mailto:') ? 'email' : 'contact_page',
      placement: supportContact.dataset.prevuraSupportContact || getPlacement(supportContact),
    });
  }
});

document.addEventListener(
  'toggle',
  (event) => {
    const details = event.target;
    if (!(details instanceof HTMLDetailsElement) || !details.open || openedDetails.has(details)) return;

    if (details.matches('[data-prevura-faq]')) {
      const published = publishPrevuraEvent('prevura:faq_opened', {
        question_key: details.dataset.prevuraFaq || 'unknown',
      });
      if (published) openedDetails.add(details);
      return;
    }

    if (details.matches('[data-prevura-participation-requirements]')) {
      const published = publishPrevuraEvent('prevura:participation_requirements_opened', {
        placement: getPlacement(details),
      });
      if (published) openedDetails.add(details);
    }
  },
  true
);

document.addEventListener(
  'invalid',
  (event) => {
    const field = event.target;
    if (!(field instanceof HTMLInputElement || field instanceof HTMLSelectElement || field instanceof HTMLTextAreaElement)) {
      return;
    }

    const now = Date.now();
    const lastPublishedAt = invalidFieldTimestamps.get(field) || 0;
    if (now - lastPublishedAt < 1500) return;
    invalidFieldTimestamps.set(field, now);

    let fieldCategory = field.type || field.tagName.toLowerCase();
    if (field.matches('[data-prevura-participation-confirmation]')) {
      fieldCategory = 'participation_confirmation';
    } else if (field.matches('[data-prevura-digital-content-consent]')) {
      fieldCategory = 'digital_content_consent';
    } else if (field.autocomplete === 'email' || field.type === 'email') {
      fieldCategory = 'email';
    }

    publishPrevuraEvent('prevura:form_error', {
      field_category: fieldCategory,
      placement: getPlacement(field),
    });
  },
  true
);

